import React, {Dispatch, FC, useCallback, useEffect, useState} from 'react';
import {Alert, Button, Card, Col, Form, Modal, Row} from "react-bootstrap";
import {FaExpandAlt} from "react-icons/fa";
import {PatternFormat} from "react-number-format";
import Loading from "../Loading";
import ObjectService from "../../http/ObjectService";
import {FaRegTrashCan} from "react-icons/fa6";

interface PropsModal {
    visible: boolean;
    setVisible: Dispatch<boolean>;
    objectAnketaId: string;
    refreshTable: () => void;
    objects: any;
}

const ObjectAnketaModalEdite: FC<PropsModal> = (props) => {
    const [objectAnketa, setObjectAnketa] = useState<any | null>(null);
    const [objectId, setObjectId] = useState<any | null>(null);

    // Поля формы
    const [lastname, setLastname] = useState<string>("");
    const [firstname, setFirstname] = useState<string>("");
    const [middlename, setMiddlename] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [birthDate, setBirthDate] = useState<string>("");
    const [comment, setComment] = useState<string>("");

    // Работа с изображениями
    const [images, setImages] = useState<File[]>([]);
    const [imagesInfo, setImagesInfo] = useState<any[]>([]);
    const [imagesDelete, setImagesDelete] = useState<any[]>([]);

    // Статусы
    const [error, setError] = useState<string | null>(null);
    const [errorFiles, setErrorFiles] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [validated, setValidated] = useState(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);

    const fetchObjectAnketa = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await ObjectService.queryAnketa({
                includes: ["categories", "photos"],
                options: { where: { id: props.objectAnketaId } }
            });

            if (data && data.length > 0) {
                const objectAnketaData = data[0];
                if (objectAnketaData.action === "DELETE") {
                    setError('Анкета объекта была удалена');
                    setNeedsRefresh(true);
                }

                setLastname(objectAnketaData.lastname);
                setFirstname(objectAnketaData.firstname);
                setMiddlename(objectAnketaData.middlename || "");
                setPosition(objectAnketaData.position || "");
                setPhone(objectAnketaData.phone || "");
                setEmail(objectAnketaData.email || "");
                setBirthDate(objectAnketaData.birthDate ? objectAnketaData.birthDate.slice(0, 10) : "");
                setComment(objectAnketaData.comment || "");
                setObjectId(objectAnketaData.objectId);
                setObjectAnketa(objectAnketaData);
            } else {
                setNeedsRefresh(true);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [props.objectAnketaId]);

    useEffect(() => {
        if (props.visible) {
            void fetchObjectAnketa();
        }
    }, [props.visible, fetchObjectAnketa]);

    const handleSubmitEdite = async (event: React.SubmitEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        const formData = new FormData();
        formData.append('id', String(objectAnketa.id));
        formData.append('lastname', lastname);
        formData.append('firstname', firstname);
        formData.append('middlename', middlename);
        formData.append('position', position);
        formData.append('phone', phone);
        formData.append('email', email);
        if (birthDate) formData.append('birthDate', birthDate);
        formData.append('comment', comment);
        formData.append('objectId', String(objectId));
        formData.append('imagesInfo', JSON.stringify(imagesInfo));
        formData.append('imagesDelete', JSON.stringify(imagesDelete));
        images.forEach(image => formData.append('images', image));

        try {
            const { data } = await ObjectService.updateAnketa(formData);
            if (data) {
                setSuccess("Анкета объекта изменена");
                setError(null);
                setNeedsRefresh(true);
                setImages([]);
                setImagesInfo([]);
                setImagesDelete([]);
            } else {
                setWarning("Изменения не зафиксированы");
            }
            await fetchObjectAnketa();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        setErrorFiles(null);
        const targetFiles = event.target.files;
        if (!targetFiles) return;

        const currentPhotosCount = objectAnketa?.photos?.length || 0;
        if (targetFiles.length + currentPhotosCount > 10) {
            setErrorFiles("Максимальное количество фотографий — 10");
            return;
        }

        const filesArray = Array.from(targetFiles);
        setImages(filesArray);
        setImagesInfo([]);

        filesArray.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const img = new Image();
                img.onload = () => {
                    setImagesInfo(prev => [...prev, {
                        name: file.name,
                        width: img.width,
                        height: img.height,
                    }]);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    }

    const handleDeleteExistingImage = (imageId: number | string) => {
        const photoToDelete = objectAnketa.photos.find((p: any) => p.id === imageId);
        if (photoToDelete) {
            setImagesDelete(prev => [...prev, photoToDelete]);
            setObjectAnketa({
                ...objectAnketa,
                photos: objectAnketa.photos.filter((p: any) => p.id !== imageId)
            });
        }
    };

    const handleHideModal = () => {
        if (needsRefresh) props.refreshTable();
        props.setVisible(false);
    };


    return (
        <Modal show={props.visible} size={"xl"} fullscreen={fullscreen ? true : "md-down"} scrollable={true} onHide={handleHideModal}>


            <div className={"modal-header"}>
                <h5 className={"mb-0"}>Редактирование анкеты объекта</h5>
                <div className={"d-flex gap-3 ai-center"}>
                    <button type={"button"} className={"btn-link"} onClick={() => setFullscreen(!fullscreen)}>
                        <FaExpandAlt/>
                    </button>
                    <button type={"button"} className={"btn-close"} onClick={handleHideModal}></button>
                </div>
            </div>

            {loading
                ?
                <div style={{height: "200px"}}><Loading/></div>
                :
                <>
                    {objectAnketa
                        ?
                        <Form noValidate validated={validated} onSubmit={handleSubmitEdite}>
                            <Modal.Body>
                                {success && <Alert variant={"success"} dismissible={true}>{success}</Alert>}
                                {warning && <Alert variant={"warning"} dismissible={true}>{warning}</Alert>}
                                {error && <Alert variant={"danger"} dismissible={true}>{error}</Alert>}

                                {objectAnketa.action !== "DELETE" && (
                                    <>
                                        <Form.Group as={Row} className="mb-3" controlId="object">
                                            <Form.Label column sm="2">Объект <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Select required={true} isInvalid={!objectId}
                                                             defaultValue={objectId}
                                                             onChange={(e) => setObjectId(e.target.value)}>
                                                    <option value={""}>-- Выберите --</option>
                                                    {props.objects.map((object: any) => {
                                                        return (
                                                            <option key={object.id}
                                                                    value={object.id}>{object.name}</option>
                                                        )
                                                    })}
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>

                                                {(() => {
                                                    if (objectId) {
                                                        const object = props.objects.find((o: any) => o.id === Number(objectId));
                                                        if (object) {
                                                            return (
                                                                <>
                                                                    <Card>
                                                                        <Card.Body>
                                                                            <Row>
                                                                                <Col sm={"6"}>
                                                                                    <div
                                                                                        className={"fs-10 text-muted"}>Категория
                                                                                    </div>
                                                                                    <div
                                                                                        className={"mb-2"}>{object?.category?.name}</div>
                                                                                    <div
                                                                                        className={"fs-10 text-muted"}>Район
                                                                                    </div>
                                                                                    <div>{object.district}</div>
                                                                                </Col>
                                                                                <Col sm={"6"}>
                                                                                    <div
                                                                                        className={"fs-10 text-muted"}>Адрес
                                                                                    </div>
                                                                                    <div
                                                                                        className={"mb-2"}>{object.address}</div>
                                                                                    <div
                                                                                        className={"fs-10 text-muted"}>Комментарий
                                                                                    </div>
                                                                                    <div>{object.comment}</div>
                                                                                </Col>
                                                                            </Row>

                                                                        </Card.Body>
                                                                    </Card>
                                                                </>
                                                            );
                                                        }
                                                    }

                                                    return null;
                                                })()}

                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="lastname">
                                            <Form.Label column sm="2">Фамилия <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={lastname}
                                                              onChange={(e) => {
                                                                  setLastname(e.target.value)
                                                              }}
                                                              required={true}

                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="firstname">
                                            <Form.Label column sm="2">Имя <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={firstname}
                                                              onChange={(e) => {
                                                                  setFirstname(e.target.value)
                                                              }}
                                                              required={true}

                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="middlename">
                                            <Form.Label column sm="2">Отчество</Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={middlename}
                                                              onChange={(e) => {
                                                                  setMiddlename(e.target.value)
                                                              }}

                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="position">
                                            <Form.Label column sm="2">Должность <span className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={position}
                                                              onChange={(e) => {
                                                                  setPosition(e.target.value)
                                                              }}
                                                              required={true}

                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="email">
                                            <Form.Label column sm="2">E-mail <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="email" autoComplete={"off"}
                                                              value={email}
                                                              onChange={(e) => {
                                                                  setEmail(e.target.value)
                                                              }}
                                                              required={true}
                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="phone">
                                            <Form.Label column sm="2">Телефон <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <PatternFormat
                                                    required={true}
                                                    formNoValidate={false}
                                                    id="phone"
                                                    format="+7 (###) ###-##-##"
                                                    mask="_"
                                                    value={phone}
                                                    className="form-control"
                                                    onValueChange={(values) => {
                                                        if (values.value.length === 10) {
                                                            setPhone(values.formattedValue)
                                                        }
                                                    }}
                                                    placeholder="+7 (___) ___-____"
                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="birthDate">
                                            <Form.Label column sm="2">Дата рождения</Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="date" autoComplete={"off"}
                                                              value={birthDate}
                                                              onChange={(e) => setBirthDate(e.target.value)}
                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="comment">
                                            <Form.Label column sm="2">Фотографии (до 10)</Form.Label>
                                            <Col sm="10">
                                                {errorFiles && <Alert variant={"danger"} dismissible={true}>{errorFiles}</Alert>}
                                                <Form.Control type="file" multiple={true} accept={"image/*"}
                                                              onChange={handleSelectFile}
                                                />

                                                <Form.Text className="text-muted ps-2">Можно выбрать несколько
                                                    файлов</Form.Text>
                                                {objectAnketa.photos
                                                    ?
                                                    <div className={"d-flex gap-3 my-3"}>
                                                        {objectAnketa.photos && objectAnketa.photos.map((photo: any) => {
                                                            const srcUrl = import.meta.env.MODE === 'development' ? import.meta.env.VITE_SERVER_URL_DEV :
                                                                "/"
                                                            return (
                                                                <div key={photo.id}
                                                                     className={"d-flex ai-center flex-column gap-3"}>
                                                                    <img alt={""} height={100}
                                                                         src={`${srcUrl}${photo.path}`}/>
                                                                    <FaRegTrashCan title={"Удалить фото"} size={16}
                                                                                   className={"pointer"}
                                                                                   onClick={() => handleDeleteExistingImage(photo.id)}
                                                                    />
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                    :
                                                    <i className={"text-muted fs-11"}>Не указан</i>
                                                }
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="comment">
                                            <Form.Label column sm="2">Комментарий</Form.Label>
                                            <Col sm="10">
                                                <Form.Control as="textarea" autoComplete={"off"} rows={4}
                                                              value={comment}
                                                              onChange={(e) => {
                                                                  setComment(e.target.value)
                                                              }}
                                                />
                                            </Col>
                                        </Form.Group>
                                    </>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                {objectAnketa.action !== "DELETE" && (<Button variant="warning" type="submit">Изменить</Button>)}
                                <Button variant="secondary" onClick={handleHideModal}>
                                    {objectAnketa.action === "DELETE" || success ? "Закрыть" : "Отмена"}
                                </Button>
                            </Modal.Footer>
                        </Form>
                        :
                        <>
                            <Modal.Body>
                                <Alert variant={"warning"}>Анкета объекта не найдена</Alert>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleHideModal}>Отмена</Button>
                            </Modal.Footer>
                        </>
                    }
                </>
            }
        </Modal>
    );
};

export default ObjectAnketaModalEdite;